import './Modals.scss';
import { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { LeaveRequest, ModalProps } from '../../utils/types';
import {
  fetchData,
  fixedTableScrollY,
  formatEnumLabel,
  getRequestStatusType,
  Notify,
} from '../../utils/constants';
import { useUser } from '../../context/UserContext';

type RequestsModalProps = ModalProps & {
  type: 'Approved' | 'Rejected' | 'Cancelled' | 'Pending' | 'PendingCancelled';
};

const filterStatusMap: Record<string, string> = {
  Approved: 'APPROVED',
  Rejected: 'REJECTED',
  Cancelled: 'CANCELLED',
  Pending: 'AWAITING_MANAGER',
  PendingCancelled: 'AWAITING_MANAGER_CANCELLATION',
};

const RequestsTeamModal = ({ isOpen, setIsOpen, type }: RequestsModalProps) => {
  const { roles } = useUser();
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const normalizedRoles = roles.map((role) => role.replace(/^ROLE_/, ''));
  const isAdmin = normalizedRoles.includes('ADMIN');
  const isHR = normalizedRoles.includes('HR');
  const isManager = normalizedRoles.includes('MANAGER');

  const fetchTableData = useCallback(async () => {
    const requestsEndpoint =
      isAdmin || isHR
        ? `${process.env.REACT_APP_API_URL}/getAllLeaveRequests`
        : isManager
          ? `${process.env.REACT_APP_API_URL}/getRequestsByTeam`
          : null;

    if (!requestsEndpoint) {
      setLeaveRequests([]);
      return;
    }

    try {
      setLoading(true);
      await fetchData(requestsEndpoint, setLeaveRequests);
    } catch (error) {
      console.error('Error while fetching team requests:', error);
      Notify('error', 'Failed to fetch team requests.');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isHR, isManager]);

  useEffect(() => {
    if (!isOpen) return;
    fetchTableData();
  }, [fetchTableData, isOpen]);

  const filteredRequests = leaveRequests.filter(
    (lq) => lq.requestStatus === filterStatusMap[type],
  );

  const columns: ColumnsType<LeaveRequest> = [
    {
      title: 'Name',
      dataIndex: 'employeeName',
      key: 'employeeName',
    },
    {
      title: 'Request Type',
      dataIndex: 'requestType',
      key: 'requestType',
      render: (value: string) => formatEnumLabel(value),
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
    },
    {
      title: 'Substitute',
      dataIndex: 'substituteName',
      key: 'substituteName',
    },
    {
      title: 'Status',
      dataIndex: 'requestStatus',
      key: 'requestStatus',
      render: (value: string) => (
        <span className={getRequestStatusType(value)}>
          {formatEnumLabel(value)}
        </span>
      ),
    },
  ];

  return (
    <Modal
      className="md"
      open={isOpen}
      title={`${type} Requests`}
      onCancel={() => setIsOpen(false)}
      footer={[
        <Button key="close" onClick={() => setIsOpen(false)}>
          Close
        </Button>,
      ]}
    >
      <Typography.Paragraph />
      <Table
        className="view-table"
        columns={columns}
        dataSource={filteredRequests}
        scroll={{ y: fixedTableScrollY }}
        loading={{ spinning: loading, tip: 'Loading leave requests...' }}
        pagination={false}
        rowKey={(record) => `${record.id}-${record.requestType}`}
        locale={{
          emptyText: `No ${type.toLowerCase()} leave requests found.`,
        }}
      />
    </Modal>
  );
};

export default RequestsTeamModal;
